import UserService from '../../services/users'
import { clearRequestCache } from '../../services/baseRequest'
import { clearCompanySession, clearProjectSession, stampSessionCompany } from '../../helpers/companySession'
import { getDeviceToken, rememberDevice, clearDeviceChallenge } from '../../helpers/authDevice'

export const state = {
    currentUser: sessionStorage.getItem('authUser'),
}

export const mutations = {
    SET_CURRENT_USER(state, newValue) {
        if (newValue && newValue.user) {
            normalizeUserCompanies(newValue.user)
        }
        state.currentUser = newValue
        saveState('auth.currentUser', newValue)
    },
    // Records the last company chosen (multi-company users): where a new tab
    // starts. The company each tab works on is in its own sessionStorage.
    SET_CURRENT_COMPANY(state, company) {
        const stored = JSON.parse(window.localStorage.getItem('auth.currentUser'))
        if (stored && stored.user) {
            stored.user.company = company
            // Keep the full membership list intact so switching away from the
            // home company (or any company) never drops it from the selector.
            normalizeUserCompanies(stored.user)
            state.currentUser = stored
            saveState('auth.currentUser', stored)
        }
    },
}

export const getters = {
    // Whether the user is currently logged in.
    loggedIn(state) {
        return !!state.currentUser
    },
}

export const actions = {
    // This is automatically run in `src/state/store.js` when the app
    // starts, along with any other actions named `init` in other modules.
    // eslint-disable-next-line no-unused-vars
    init({ state, dispatch }) {
        dispatch('validate')
    },

    // Logs in the current user.
    logIn({ commit, dispatch, getters }, { email, password, turnstileToken, lang } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        return new Promise((resolve, reject) => {
            const payload ={
                email,
                password,
                turnstileToken,
                lang,
                deviceToken: getDeviceToken(email)
            }
            UserService.login(payload).then((response) => {
                const user = response.data.data

                if (!user.needsDeviceVerification) completeSession(commit, user)
                resolve(user);
            }).catch(error => { reject(error)  })
        });

    
        
    },

    verifyDevice({ commit }, payload) {
        return UserService.verifyDevice(payload).then(response => {
            const session = response.data.data
            completeSession(commit, session)
            return session
        })
    },

    resendDeviceCode(context, payload) {
        return UserService.resendDeviceCode(payload).then(response => response.data.data)
    },

    // Invitation link: tells a valid invitation from a used/expired one
    // eslint-disable-next-line no-unused-vars
    getInviteInfo({ commit }, payload) {
        return new Promise((resolve, reject) => {
            UserService.getInviteInfo(payload).then((response) => {
                resolve(response.data.data)
            }).catch(error => { reject(error) })
        });
    },

    // Sets the password chosen by the invitee; the backend answers with a session,
    // so the collaborator gets in without a second trip through the login
    acceptInvite({ commit, dispatch, getters }, payload) {
        return new Promise((resolve, reject) => {
            UserService.acceptInvite(payload).then((response) => {
                const user = response.data.data
                // The link may be opened on a browser where someone else is signed in
                const closing = getters.loggedIn ? dispatch('logOut') : Promise.resolve()

                closing.then(() => {
                    commit('SET_CURRENT_USER', user)
                    clearProjectSession()
                    stampSessionCompany(user && user.user ? user.user.company : null)
                    resolve(user)
                })
            }).catch(error => { reject(error) })
        });
    },

    // Switches the company THIS TAB works on, when the user belongs to more than
    // one. Other tabs keep theirs: each request names its tab's company
    // (see helpers/companySession). The project and anything cached for the
    // previous company go with it, so nothing of one company is read or written
    // under the other.
    selectCompany({ commit }, companyId) {
        return new Promise((resolve, reject) => {
            UserService.selectCompany(companyId).then((response) => {
                const company = response.data.data

                commit('SET_CURRENT_COMPANY', company)
                clearProjectSession()
                stampSessionCompany(company || companyId)
                clearRequestCache()
                resolve(company)
            }).catch(error => { reject(error) })
        });
    },

    // Logs out the current user.
    // eslint-disable-next-line no-unused-vars
    logOut({commit}) {
        UserService.logout().catch(() => {})

        commit('SET_CURRENT_USER', null)
        localStorage.removeItem('auth.currentUser');
        clearCompanySession();

        return Promise.resolve();
    },

    // eslint-disable-next-line no-unused-vars
    request_reset_password({commit},payload) {
        return new Promise((resolve,reject) => {
            UserService.requestPasswordReset(payload).then(() => {
                resolve();
            }).catch(error => { reject(error)})
        });
    },

    // eslint-disable-next-line no-unused-vars
    password_reset({commit},payload) {
        return new Promise((resolve,reject) => {
            UserService.passwordReset(payload).then((res) => {
                resolve(res);
            }).catch(error => { reject(error)})
        });
    },


    // register the user
    register({ dispatch, getters } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        /*return getFirebaseBackend().registerUser(email, password).then((response) => {
            const user = response
            commit('SET_CURRENT_USER', user)
            return user
        });*/
    },

    // eslint-disable-next-line no-unused-vars
    resetPassword({ commit, dispatch, getters }, { email } = {}) {
        if (getters.loggedIn) return dispatch('validate')

        /*return getFirebaseBackend().forgetPassword(email).then((response) => {
            const message = response.data
            return message
        });*/
    },

    // Validates the current user's token and refreshes it
    // with new data from the API.
    // eslint-disable-next-line no-unused-vars
    validate({ commit, state }) {
        if (!state.currentUser) return Promise.resolve(null)
        /*const user = getFirebaseBackend().getAuthenticatedUser();
        commit('SET_CURRENT_USER', user)
        return user;*/
    },
}

// ===
// Private helpers
// ===

function saveState(key, state) {
    window.localStorage.setItem(key, JSON.stringify(state))
}

function completeSession(commit, session) {
    if (!session || !session.token || !session.user) throw new Error('Invalid auth response')
    rememberDevice(session)
    // Only /auth/login receives device credentials, not ordinary API requests.
    const { deviceToken, deviceExpiresAt, ...authSession } = session // eslint-disable-line no-unused-vars
    commit('SET_CURRENT_USER', authSession)
    clearDeviceChallenge()
    clearProjectSession()
    stampSessionCompany(session.user.company)
}

// Ensures `user.companies` holds every company the user can operate on
// (the active/home `user.company` plus all memberships), deduped by id.
// This keeps the company selector complete even after switching companies,
// because `SET_CURRENT_COMPANY` overwrites `user.company` with the active one.
function normalizeUserCompanies(user) {
    if (!user || typeof user !== 'object') {
        return
    }

    const list = []
    const seen = {}
    const add = (company) => {
        if (!company) {
            return
        }
        const rawId = typeof company === 'object' ? (company._id || company.id) : company
        if (!rawId) {
            return
        }
        const id = rawId.toString()
        if (seen[id]) {
            return
        }
        seen[id] = true
        list.push(company)
    }

    add(user.company)
    if (Array.isArray(user.companies)) {
        user.companies.forEach(add)
    }

    user.companies = list
}
